<template>
  <div>
    <validation-observer
      ref="formObserver"
      #default="{ invalid }"
    >
      <div class="v2-product-edit__body">
        <div class="v2-product-edit__top-section">
          <div class="v2-product-edit__main-row">
            <v2-images-section />
            <div class="v2-product-edit__input-right">
              <v2-basic-information />
              <v2-pricing ref="pricingComponent" />
            </div>
          </div>
          <v2-product-inventory />
          <v2-additional-info />
        </div>

        <div class="v2-product-edit__footer">
          <v2-button
            variant="outline"
            data-test="close-btn"
            @click="handleClose"
          >
            Close
          </v2-button>
          <v2-button
            :disabled="isSaveDisabled || invalid"
            :loading="isSaving"
            data-test="save-btn"
            @click="handleSave"
          >
            {{ isSaved ? 'Saved' : 'Save' }}
          </v2-button>
        </div>
      </div>
    </validation-observer>

    <!-- Unsaved changes confirmation -->
    <v2-confirmation-modal
      v-if="isCloseModalVisible"
      :visible="isCloseModalVisible"
      data-test="unsaved-confirm"
      @ok="handleCloseConfirm"
      @close="handleConfirmHide"
    />

    <!-- Success toast -->
    <transition name="v2-fade">
      <div
        v-if="showSuccessToast"
        class="v2-product-edit__toast"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span>{{ updatedPricesText }} for {{ updatedOrdersCount }} selected orders were updated successfully!</span>
      </div>
    </transition>
  </div>
</template>

<script>
import store from '@/store'
import {
  CHECK_PRODUCT_ORDERS,
  RESET_EDIT_STORE,
  UPDATE_PRODUCT,
  UPLOAD_NEW_PRODUCT_IMAGE,
} from '@/store/modules/productEdit.module'
import { ValidationObserver } from 'vee-validate'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import V2Button from '@/components/v2/V2Button.vue'
import V2ImagesSection from './V2ImagesSection.vue'

export default {
  name: 'V2ProductEditContent',
  components: {
    ValidationObserver,
    V2Button,
    V2ImagesSection,
    V2BasicInformation: () => import('./V2BasicInformation.vue'),
    V2Pricing: () => import('./V2Pricing.vue'),
    V2ProductInventory: () => import('./V2ProductInventory.vue'),
    V2AdditionalInfo: () => import('./V2AdditionalInfo.vue'),
    V2ConfirmationModal: () => import('./V2ConfirmationModal.vue'),
  },
  props: {
    presentation: {
      type: String,
      default: 'modal',
    },
    productId: {
      type: String,
      default: '',
    },
    collectionId: {
      type: String,
      default: '',
    },
    wishlistId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isSaving: false,
      isSaved: false,
      isCloseModalVisible: false,
      updatedOrdersCount: 0,
      showSuccessToast: false,
      updatedPricesText: '',
    }
  },
  computed: {
    changedFields() {
      return store.state.productEdit.changedFields || []
    },
    isDirty() {
      return this.changedFields.length > 0
    },
    isSaveDisabled() {
      if (this.isSaved) return true
      return this.changedFields.length === 0 || this.isSaving || !this.areImagesValid
    },
    areImagesValid() {
      return store.state.productEdit.productImages.length > 0
    },
  },
  watch: {
    changedFields(newValue) {
      if (newValue.length > 0 && this.isSaved) {
        this.isSaved = false
      }
    },
  },
  beforeDestroy() {
    store.commit(RESET_EDIT_STORE)
  },
  methods: {
    requestUnsavedConfirm() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_VIEWS_CONFIRMATION_MODAL, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        formData: store.state.productEdit.newData,
      })
      this.isCloseModalVisible = true
    },
    handleConfirmHide() {
      this.isCloseModalVisible = false
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLOSES_CONFIRMATION_MODAL, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        formData: store.state.productEdit.newData,
      })
    },
    handleCloseConfirm() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_OK_ON_CONFIRMATION_MODAL, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        formData: store.state.productEdit.newData,
      })
      this.isCloseModalVisible = false
      this.$emit('request-close')
    },
    handleClose() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_CLOSE_BUTTON, {
        product: store.state.productEdit.savedState,
      })
      if (this.changedFields.length > 0) {
        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_VIEWS_CONFIRMATION_MODAL, {
          productId: store.state.productEdit.savedState._id,
          collectionId: store.state.productEdit.savedState.collectionId,
          formData: store.state.productEdit.newData,
        })
        this.isCloseModalVisible = true
      } else {
        this.$emit('request-close')
      }
    },
    async _uploadSingleImage(image) {
      const formData = new FormData()
      formData.append('file', image.file)
      const uploadUrl = await store.dispatch(UPLOAD_NEW_PRODUCT_IMAGE, {
        formData,
        index: image.index,
      })
      if (!uploadUrl) {
        throw new Error('Failed to upload one or more images.')
      }
      image.isLocal = false
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_UPLOAD_IMAGE_SUCCESS, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        imageIndex: image.index,
        imageUrl: uploadUrl,
      })
    },
    _trackImageUploadFailure(image) {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_UPLOAD_IMAGE_FAILED, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        imageIndex: image.index,
        imageUrl: image.url,
        error: 'Failed to upload one or more images.',
      })
    },
    async _tryUploadImage(image) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await this._uploadSingleImage(image)
        return true
      } catch {
        this._trackImageUploadFailure(image)
        return false
      }
    },
    async uploadLocalImages() {
      const productImages = store.state.productEdit.productImages
      const localImages = productImages.filter(img => img.isLocal)
      if (localImages.length === 0) return true

      const results = []
      for (const image of localImages) {
        // eslint-disable-next-line no-await-in-loop
        results.push(await this._tryUploadImage(image))
      }
      return results.every(Boolean)
    },
    handleSuccessfulSave() {
      this.isSaved = true
      this.showSuccessSaveToast()
      this.closeProductEditModal()
    },
    _handlePriceUpdateWithOrders(pricingComponent, response) {
      pricingComponent.showPriceUpdateWarning = true
      this.showSuccessSaveToast(response)
      this.$root.$once('orders-price-updated', this.handleOrdersPriceUpdated)
      this.$root.$once('price-update-closed', this.closeProductEditModal)
      this.isSaved = true
    },
    _trackSaveFailure(error) {
      apiToastError(error || 'Failed to update product.')
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_SAVE_FAILED, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        payload: store.state.productEdit.newData,
        error,
      })
    },
    async _saveProductAndUpdateOrders() {
      try {
        const response = await store.dispatch(UPDATE_PRODUCT)
        const pricingComponent = this.$refs.pricingComponent
        const hasPriceChange = pricingComponent?.priceChanged

        if (!hasPriceChange) {
          this.handleSuccessfulSave()
          return
        }

        const productId = store.state.productEdit.savedState._id
        const hasOrders = await store.dispatch(CHECK_PRODUCT_ORDERS, { productId })

        if (!hasOrders) {
          this.handleSuccessfulSave()
          return
        }

        this._handlePriceUpdateWithOrders(pricingComponent, response)
      } catch (error) {
        this._trackSaveFailure(error)
      }
    },
    async handleSave() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_SAVE_BUTTON, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        formData: store.state.productEdit.newData,
      })
      const isFormValid = await this.$refs.formObserver.validate()
      if (!isFormValid) return
      if (!this.areImagesValid) {
        apiToastError('Main image is required.')
        return
      }
      try {
        document.activeElement.blur()
        this.isSaving = true
        const imagesUploadSuccess = await this.uploadLocalImages()
        if (!imagesUploadSuccess) {
          apiToastError('Failed to upload one or more images.')
          return
        }
        await this._saveProductAndUpdateOrders()
      } finally {
        this.isSaving = false
      }
    },
    closeProductEditModal() {
      this.$emit('edit-success')
    },
    handleOrdersPriceUpdated({ count, prices }) {
      this.updatedOrdersCount = count
      const parts = []
      if (prices.includes('Wholesale')) parts.push('Wholesale')
      if (prices.includes('Retail')) parts.push('Retail')
      this.updatedPricesText = parts.length ? `${parts.join(' and ')} prices` : 'Prices'
      this.showSuccessToast = count
      setTimeout(() => {
        this.showSuccessToast = false
      }, 2000)
    },
    showSuccessSaveToast(response) {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_SAVE_SUCCESS, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        payload: store.state.productEdit.newData,
        response,
      })
      apiToastSuccess('Product updated successfully!')
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-product-edit {
  &__body {
    display: flex;
    flex-direction: column;
    height: calc(85vh - 80px);
  }

  &__top-section {
    flex: 1;
    overflow-y: auto;
    padding: var(--v2-space-6);
    padding-top: var(--v2-space-4);
  }

  &__main-row {
    display: flex;
    gap: var(--v2-space-6);

    @media (max-width: 900px) {
      flex-direction: column;
    }
  }

  &__input-right {
    flex: 1;
    min-width: 0;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--v2-space-4);
    padding: var(--v2-space-4) var(--v2-space-6);
    padding-bottom: 0;
    border-top: 1px solid var(--v2-border);
  }

  &__toast {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10000;
    display: flex;
    align-items: center;
    gap: var(--v2-space-4);
    padding: var(--v2-space-3) var(--v2-space-4);
    background: var(--v2-foreground);
    color: #fff;
    border-radius: var(--v2-radius-lg);
    font-size: 0.8125rem;
    font-weight: 600;
    box-shadow: var(--v2-shadow-lg);
  }
}

.v2-fade-enter-active,
.v2-fade-leave-active {
  transition: opacity 0.4s ease;
}

.v2-fade-enter,
.v2-fade-leave-to {
  opacity: 0;
}
</style>
